import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { SessionProvider, useSession } from '@/session.tsx';
import { beginSignIn, currentPath, loadAuthConfig, millisecondsUntilReauth } from '@/lib/console-auth.ts';
import type { ConsoleAuthConfig } from '@/lib/console-auth.ts';
import { SignIn } from '@/pages/SignIn.tsx';
import { Callback } from '@/pages/Callback.tsx';
import { ChooseOrg } from '@/pages/ChooseOrg.tsx';
import { FragmentsPage } from '@/pages/Fragments.tsx';
import { RegistryPage } from '@/pages/Registry.tsx';
import { LayoutPage } from '@/pages/Layout.tsx';
import { TopologyPage } from '@/pages/Topology.tsx';
import { ShellsPage } from '@/pages/Shells.tsx';
import { AuditPage } from '@/pages/Audit.tsx';
import { PreviewsPage } from '@/pages/Previews.tsx';
import { IdentityPage } from '@/pages/Identity.tsx';

/**
 * The nav, in the handoff's order — minus the screens that do not exist.
 *
 * Overview joins this list when phase 6 slice 4 builds it; Domains and Billing have no backend at
 * all. Nothing here may lead to a placeholder, so a screen earns its slot by
 * existing. Icons are gone: these designs drop them.
 *
 * Topology is last, and is the one page still rendered by the OSS component. The handoff drops it
 * without drawing a replacement, and its own instruction is to keep the component until a design
 * exists — so it keeps its slot rather than becoming a page nothing links to.
 */
const TABS = [
  { key: 'fragments', label: 'Fragments' },
  { key: 'registry', label: 'Registry' },
  { key: 'layout', label: 'Layout' },
  { key: 'shells', label: 'Shells' },
  { key: 'previews', label: 'Channels' },
  { key: 'identity', label: 'Identity' },
  { key: 'audit', label: 'Audit' },
  { key: 'topology', label: 'Topology' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * A router in twenty lines rather than a routing library.
 *
 * Eight pages under one org and no nested or dynamic routes; the paths are `/orgs/{orgId}/{tab}`.
 * A library here would be a dependency, a bundle, and an abstraction standing between six `if`s
 * and the reader.
 */
function useTab(orgId: string | undefined): [TabKey, (tab: TabKey) => void] {
  const [tab, setTab] = useState<TabKey>(() => tabFromPath(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setTab(tabFromPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return [
    tab,
    (next) => {
      setTab(next);
      if (orgId) window.history.pushState(null, '', `/orgs/${orgId}/${next}`);
    },
  ];
}

function tabFromPath(pathname: string): TabKey {
  const match = /^\/orgs\/[^/]+\/(\w+)/.exec(pathname);
  const candidate = match?.[1];
  return TABS.some((tab) => tab.key === candidate) ? (candidate as TabKey) : 'fragments';
}

/**
 * The pool's configuration, fetched once at boot.
 *
 * `undefined` while loading and `null` when there is none — a distinction the sign-in page needs,
 * because rendering the token form as though sign-in were unavailable, only to have the button
 * appear a moment later, is worse than a brief blank.
 */
function useAuthConfig(): ConsoleAuthConfig | null | undefined {
  const [config, setConfig] = useState<ConsoleAuthConfig | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void loadAuthConfig().then((loaded) => { if (live) setConfig(loaded); });
    return () => { live = false; };
  }, []);

  return config;
}

/**
 * Re-auth, a minute before the ID token expires (§7).
 *
 * Not a refresh token — there is nowhere safe to keep one (§0.2 rule 2). A redirect through the
 * pool costs one round trip the user does not see, because the pool's own session cookie answers
 * without a prompt. Doing it *ahead* of expiry is what keeps it invisible: after expiry the user
 * would first watch a page fail.
 */
function useSilentReauth(credential: { kind: string; expiresAt?: number } | null, config: ConsoleAuthConfig | null | undefined) {
  useEffect(() => {
    if (!config || credential?.kind !== 'console' || !credential.expiresAt) return;

    const timer = setTimeout(() => {
      void beginSignIn(config, currentPath());
    }, millisecondsUntilReauth(credential.expiresAt));

    return () => clearTimeout(timer);
  }, [credential, config]);
}

function Console() {
  const { credential, session, signOut, leaveOrg } = useSession();
  const [tab, setTab] = useTab(session?.orgId);
  const authConfig = useAuthConfig();

  useSilentReauth(credential, authConfig);

  // The callback owns its own path, before any gate: it is how a credential is obtained, so it
  // cannot be behind one.
  if (window.location.pathname === '/callback') {
    return authConfig === undefined ? null : <Callback authConfig={authConfig} />;
  }

  if (!credential) return authConfig === undefined ? null : <SignIn authConfig={authConfig} />;
  if (!session) return <ChooseOrg />;

  return (
    <div className="min-h-dvh">
      {/* The masthead. A 5px rule, a baseline row, a 1px rule, the nav, and a hairline — the
          hierarchy is the rules and the whitespace, so there is no card and no shadow here. */}
      <header className="px-5 pt-5 sm:px-10">
        <div className="bg-foreground h-[5px]" />

        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2.5">
          <span className="text-[22px] leading-none font-semibold tracking-[-0.02em]">Braid Cloud</span>

          {/* The dateline. A console session belongs to a person who may be in several orgs, so
              the org stays a control rather than becoming a label; an API token names exactly one,
              and offering to switch would lead somewhere it cannot go. */}
          {credential?.kind === 'console' ? (
            <button
              type="button"
              onClick={leaveOrg}
              title="Switch organisation"
              className="text-muted-foreground hover:text-foreground text-[11px] tracking-[0.14em] uppercase transition-colors"
            >
              {session.orgName} · <span className="font-mono">{session.orgSlug ?? session.orgId}</span>
            </button>
          ) : (
            <span className="text-muted-foreground text-[11px] tracking-[0.14em] uppercase">
              {session.orgName} · <span className="font-mono">{session.orgSlug ?? session.orgId}</span>
            </span>
          )}

          <div className="flex-1" />

          {credential?.email ? <span className="text-muted-foreground text-xs">{credential.email}</span> : null}
          {/* The role decides what half these buttons will do. Showing it turns a surprise 403
              into something the operator knew before they clicked. */}
          <Badge variant={session.role === 'viewer' ? 'secondary' : 'default'}>{session.role}</Badge>
          <Button variant="ghost" size="sm" onClick={signOut}>
            Sign out
          </Button>
        </div>

        <div className="bg-foreground h-px" />

        <nav aria-label="Sections">
          <ul className="-mb-px flex gap-[26px] overflow-x-auto">
            {TABS.map((entry) => {
              const active = tab === entry.key;
              return (
                <li key={entry.key}>
                  {/* An anchor, not a button: middle-click, "copy link", and open-in-new-tab are
                      browser affordances an operator expects, and the URLs already work. */}
                  <a
                    href={session.orgId ? `/orgs/${session.orgId}/${entry.key}` : '#'}
                    onClick={(event) => {
                      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                      event.preventDefault();
                      setTab(entry.key);
                    }}
                    aria-current={active ? 'page' : undefined}
                    className={[
                      'block border-b-2 pt-2.5 pb-2 text-sm whitespace-nowrap transition-colors',
                      active
                        ? 'border-primary text-primary font-semibold'
                        : 'text-muted-foreground hover:text-foreground border-transparent',
                    ].join(' ')}
                  >
                    {entry.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-border border-t" />
      </header>

      <main className="px-5 py-8 sm:px-10">
        {tab === 'fragments' && <FragmentsPage />}
        {tab === 'registry' && <RegistryPage />}
        {tab === 'layout' && <LayoutPage />}
        {tab === 'topology' && <TopologyPage />}
        {tab === 'shells' && <ShellsPage />}
        {tab === 'previews' && <PreviewsPage />}
        {tab === 'identity' && <IdentityPage />}
        {tab === 'audit' && <AuditPage />}
      </main>
    </div>
  );
}

export function App() {
  return (
    <SessionProvider>
      <Console />
    </SessionProvider>
  );
}
